/**
 * Parse a provider `response.done` event into {@link AgentResponseDone}; null
 * when it carries no id. Shared so both transports read it identically.
 */
export function parseResponseDone(ev) {
    const r = ev.response;
    if (!r?.id)
        return null;
    const err = r.status_details?.error;
    return {
        responseId: r.id,
        status: r.status ?? 'completed',
        reason: r.status_details?.reason,
        errorCode: err?.code,
        errorType: err?.type,
        errorMessage: err?.message,
        ...(r.usage
            ? { usage: { inputTokens: r.usage.input_tokens ?? 0, outputTokens: r.usage.output_tokens ?? 0, totalTokens: r.usage.total_tokens ?? 0 } }
            : {}),
    };
}
/**
 * Speech that ANSWERS: it handed the turn back with a question ("…which time
 * suits you?", "anything else?"). Length is no evidence — a long announcement
 * ("let me see what we have free tomorrow for you") would leave the caller
 * waiting in silence, and a missed answer only costs the old second response.
 */
export function spokeAnAnswer(text) {
    return /[?？؟]/.test(text);
}
/** Higher replaces lower when both want the next response. */
const PRIORITY = {
    reprompt: 0,
    greeting: 1,
    tool_continuation: 2,
    caller_turn: 3,
    closing_rescue: 4,
};
/** Retries a rate-limited request gets at least (still within its wait budget). */
const RATE_LIMIT_MIN_RETRIES = 4;
const RETRYABLE_CODES = new Set(['rate_limit_exceeded', 'server_error', 'server_overloaded']);
export class ResponseCoordinator {
    wire;
    clock;
    policy;
    logger;
    events;
    wanted = null;
    inflight = null;
    active = null;
    batches = new Map();
    /** Tool calls seen before their response was known (no response id). */
    orphanTools = new Map();
    retry = null;
    cancelled = new Set();
    seq = 0;
    closed = false;
    /** While the caller is mid-utterance nothing new may start: a continuation
     * would talk over them, and their turn will be answered anyway. */
    callerSpeaking = false;
    constructor(wire, clock, policy, logger, events = {}) {
        this.wire = wire;
        this.clock = clock;
        this.policy = policy;
        this.logger = logger;
        this.events = events;
    }
    // ── Queries ────────────────────────────────────────────────────────────────
    /** Nothing requested, generating, retrying or waiting on a tool. */
    get idle() {
        return (this.wanted === null &&
            this.inflight === null &&
            this.active === null &&
            this.retry === null &&
            this.batches.size === 0 &&
            [...this.orphanTools.values()].every((t) => t.settled));
    }
    /** The response currently generating, if any. */
    get activeResponseId() {
        return this.active?.responseId ?? null;
    }
    get activeIntent() {
        if (!this.active)
            return null;
        return this.active.want?.intent ?? 'auto';
    }
    /** Audio from a cancelled response must never reach the caller. */
    isCancelled(responseId) {
        return responseId !== null && this.cancelled.has(responseId);
    }
    // ── Requests ───────────────────────────────────────────────────────────────
    /** Ask for a response of this intent. Coalesces with anything already wanted. */
    request(intent, request = {}) {
        if (this.closed)
            return;
        // A reprompt is only for silence: if anything else is going on, it is moot.
        if (intent === 'reprompt' && (this.inflight || this.active || this.wanted || this.retry))
            return;
        // A new caller turn supersedes a scheduled retry of anything lower.
        if (this.retry && PRIORITY[intent] >= PRIORITY[this.retry.want.intent])
            this.cancelRetry();
        if (!this.wanted || PRIORITY[intent] >= PRIORITY[this.wanted.intent]) {
            this.wanted = { intent, request, attempts: 0, firstAt: this.clock.now().getTime() };
        }
        this.pump();
    }
    /** The caller started speaking: stop any retry or queued nudge — what they
     * say next is what gets answered. Generation cancellation is the session's
     * call (it knows whether audio is still playing). */
    onCallerSpeechStarted() {
        this.callerSpeaking = true;
        this.cancelRetry();
        if (this.wanted && (this.wanted.intent === 'reprompt' || this.wanted.intent === 'greeting'))
            this.wanted = null;
        this.changed();
    }
    /** The detector says the caller stopped (or gave up waiting for them to). */
    onCallerSpeechStopped() {
        this.callerSpeaking = false;
        this.pump();
    }
    /** The caller's turn is in the conversation: answer it. */
    onCallerTurnCommitted() {
        this.callerSpeaking = false;
        this.request('caller_turn');
    }
    /** Cancel whatever is generating (barge-in). */
    cancelActive() {
        const id = this.active?.responseId ?? null;
        if (id) {
            this.cancelled.add(id);
            this.wire.cancelResponse(id);
        }
        return id;
    }
    // ── Provider events ────────────────────────────────────────────────────────
    onResponseCreated(responseId) {
        if (this.closed)
            return;
        const want = this.inflight?.want ?? null;
        this.inflight?.timer();
        this.inflight = null;
        // A response we did not ask for (VAD auto-creation left on by a provider
        // default) is still tracked, so it is serialised like the rest.
        this.active?.timer();
        this.active?.silence?.();
        const firstOutputMs = this.policy.firstOutputMs ?? 0;
        this.active = {
            responseId,
            want,
            timer: this.watchdog(() => this.stalled('active', responseId)),
            silence: firstOutputMs > 0 ? this.clock.setTimer(firstOutputMs, () => this.silent(responseId)) : null,
        };
        if (!this.batches.has(responseId))
            this.batches.set(responseId, { done: false, tools: new Map(), said: '' });
        // Tools that arrived before we knew their response belong to the active one.
        this.changed();
    }
    /** The response produced something the caller will hear or a tool to run:
     * it is alive. */
    onResponseOutput(responseId) {
        if (!responseId || this.active?.responseId !== responseId || !this.active.silence)
            return;
        this.active.silence();
        this.active.silence = null;
    }
    /** The response said this (a finished audio transcript part). */
    onResponseText(responseId, text) {
        const batch = responseId ? this.batches.get(responseId) : undefined;
        if (!batch || batch.done || !text)
            return;
        batch.said = `${batch.said} ${text}`.slice(-400);
    }
    /** A tool call was emitted by `responseId` (null when the provider did not say). */
    onToolCall(callId, responseId) {
        this.onResponseOutput(responseId);
        const target = responseId ?? this.active?.responseId ?? null;
        if (target) {
            const batch = this.batches.get(target) ?? { done: false, tools: new Map(), said: '' };
            this.batches.set(target, batch);
            batch.tools.set(callId, { settled: false, respond: false });
        }
        else {
            this.orphanTools.set(callId, { settled: false, respond: false });
        }
        this.changed();
    }
    /**
     * A tool's output has been PUBLISHED. `respond` = it wants the model to speak
     * about it (false for `end_call`). The continuation goes out once the whole
     * batch is settled and its response is done.
     */
    settleTool(callId, respond) {
        for (const [responseId, batch] of this.batches) {
            const t = batch.tools.get(callId);
            if (!t)
                continue;
            t.settled = true;
            t.respond = respond;
            this.maybeContinue(responseId, batch);
            return;
        }
        const orphan = this.orphanTools.get(callId);
        if (orphan) {
            orphan.settled = true;
            orphan.respond = respond;
            if ([...this.orphanTools.values()].every((t) => t.settled)) {
                // No response to ask about: a conditional follow-up is given.
                const wantsReply = [...this.orphanTools.values()].some((t) => t.respond !== false);
                this.orphanTools.clear();
                if (wantsReply)
                    this.request('tool_continuation');
            }
        }
        this.pump();
    }
    onResponseDone(done) {
        if (this.closed)
            return;
        const isActive = this.active?.responseId === done.responseId;
        const want = isActive ? (this.active?.want ?? null) : null;
        if (isActive) {
            this.active?.timer();
            this.active?.silence?.();
            this.active = null;
        }
        const batch = this.batches.get(done.responseId);
        let retrying = false;
        if (done.status === 'failed' || (done.status === 'incomplete' && done.errorCode)) {
            this.logger.warn({
                responseId: done.responseId,
                status: done.status,
                code: done.errorCode,
                reason: done.reason,
                intent: want?.intent,
                // The provider's own words: for a rate limit, the limit, the tokens
                // used and requested, and the retry hint (doc 38).
                detail: done.errorMessage?.slice(0, 300),
            }, 'openai response did not complete');
            if (isActive && want && done.errorCode && RETRYABLE_CODES.has(done.errorCode)) {
                retrying = this.scheduleRetry(want, done.errorMessage, done.errorCode);
            }
            else if (isActive && want) {
                this.events.onGiveUp?.(want.intent, 'rejected', done.errorCode);
            }
            // A failed response's tool calls never ran; nothing to continue.
            if (batch && batch.tools.size === 0)
                this.batches.delete(done.responseId);
        }
        if (batch && this.batches.has(done.responseId)) {
            batch.done = true;
            // Only a COMPLETED response has said its answer (doc 50 F4). Cut off by
            // the caller, what it generated was not what they heard; incomplete
            // (max_output_tokens) or failed, it stopped mid-answer — a question
            // mark in the part it got out is not the end of the answer.
            if (done.status !== 'completed')
                batch.said = '';
            this.maybeContinue(done.responseId, batch);
        }
        this.cancelled.delete(done.responseId);
        this.events.onFinished?.({
            responseId: done.responseId,
            intent: want?.intent ?? 'auto',
            status: done.status,
            retrying,
        });
        this.pump();
    }
    /** A provider error about one of our client events. */
    onRequestError(err) {
        if (this.closed)
            return;
        // A stale cancel (the response had already ended) is harmless by design.
        if (err.code === 'response_cancel_not_active')
            return;
        const mine = this.inflight && err.eventId === this.inflight.eventId ? this.inflight : null;
        if (!mine) {
            // About some other event (a session.update, a stale truncate). It says
            // nothing about the response we are waiting for — clear nothing.
            this.logger.warn({ eventId: err.eventId, code: err.code, type: err.type }, 'openai request error (not a response request)');
            return;
        }
        mine.timer();
        this.inflight = null;
        const want = mine.want;
        if (err.code === 'conversation_already_has_active_response') {
            // Our bookkeeping missed a response (e.g. one the server created). Wait
            // for it to finish and send this again — not counted as a retry.
            if (!this.wanted || PRIORITY[want.intent] >= PRIORITY[this.wanted.intent])
                this.wanted = want;
        }
        else if (err.code && RETRYABLE_CODES.has(err.code)) {
            this.logger.warn({ code: err.code, intent: want.intent, detail: err.message?.slice(0, 300) }, 'openai response request failed — retrying');
            this.scheduleRetry(want, err.message, err.code);
        }
        else {
            this.logger.error({ code: err.code, type: err.type, intent: want.intent }, 'openai rejected a response request');
            this.events.onGiveUp?.(want.intent, 'rejected', err.code);
        }
        this.pump();
    }
    close() {
        this.closed = true;
        this.cancelRetry();
        this.inflight?.timer();
        this.active?.timer();
        this.active?.silence?.();
        this.inflight = null;
        this.active = null;
        this.wanted = null;
        this.batches.clear();
        this.orphanTools.clear();
    }
    // ── internals ──────────────────────────────────────────────────────────────
    maybeContinue(responseId, batch) {
        if (!batch.done)
            return;
        if ([...batch.tools.values()].some((t) => !t.settled))
            return;
        this.batches.delete(responseId);
        const answered = spokeAnAnswer(batch.said);
        if ([...batch.tools.values()].some((t) => t.respond === true || (t.respond === 'unless_answered' && !answered)))
            this.request('tool_continuation');
        else
            this.changed();
    }
    pump() {
        if (this.closed || this.inflight || this.active || this.retry || this.callerSpeaking) {
            this.changed();
            return;
        }
        // Never ask for a new response while a tool batch is unsettled: its
        // continuation must see every output (doc 33 W3 #3).
        for (const batch of this.batches.values()) {
            if (!batch.done || [...batch.tools.values()].some((t) => !t.settled)) {
                this.changed();
                return;
            }
        }
        if ([...this.orphanTools.values()].some((t) => !t.settled)) {
            this.changed();
            return;
        }
        const want = this.wanted;
        if (!want) {
            this.changed();
            return;
        }
        this.wanted = null;
        this.send(want);
    }
    send(want) {
        this.seq += 1;
        const eventId = `resp_${this.seq}_${want.intent}`;
        this.inflight = { eventId, want, timer: this.watchdog(() => this.stalled('inflight', eventId)) };
        this.wire.createResponse({ eventId, ...want.request });
        this.changed();
    }
    /** Returns true when a retry was scheduled. */
    scheduleRetry(want, message, code) {
        const attempts = want.attempts + 1;
        // A rate limit is bounded by its wait budget more than by a count: each
        // retry waits as long as the provider asked, so a few more fit (doc 38).
        // 0 still disables retries entirely.
        const maxAttempts = code === 'rate_limit_exceeded' && this.policy.maxRetries > 0 ? Math.max(this.policy.maxRetries, RATE_LIMIT_MIN_RETRIES) : this.policy.maxRetries;
        if (attempts > maxAttempts) {
            this.events.onGiveUp?.(want.intent, 'retries_exhausted');
            return false;
        }
        const hintMs = Math.ceil(Number(/try again in ([\d.]+)\s*(ms|s)/i.exec(message ?? '')?.[1] ?? 1) *
            (/try again in [\d.]+\s*ms/i.test(message ?? '') ? 1 : 1000));
        const jitter = Math.round((this.policy.random ?? Math.random)() * 250);
        const delay = Math.max(250, hintMs) + jitter;
        const waited = this.clock.now().getTime() - want.firstAt;
        // Never retry EARLIER than the provider said; if its wait does not fit in
        // what the caller can bear, give up now and let the fallback speak.
        const budget = code === 'rate_limit_exceeded' ? Math.max(this.policy.maxCallerWaitMs, this.policy.rateLimitWaitMs ?? 0) : this.policy.maxCallerWaitMs;
        if (waited + delay > budget) {
            this.events.onGiveUp?.(want.intent, 'wait_deadline');
            return false;
        }
        const next = { ...want, attempts };
        this.cancelRetry();
        const cancel = this.clock.setTimer(delay, () => {
            this.retry = null;
            if (this.closed)
                return;
            if (!this.wanted || PRIORITY[next.intent] >= PRIORITY[this.wanted.intent])
                this.wanted = next;
            this.pump();
        });
        this.retry = { want: next, cancel };
        this.events.onRetryScheduled?.(want.intent, delay, code);
        return true;
    }
    cancelRetry() {
        this.retry?.cancel();
        this.retry = null;
    }
    watchdog(cb) {
        return this.clock.setTimer(this.policy.stallMs, cb);
    }
    /** A created response stayed silent: cancel it and ask once more for the
     * same intent (bounded by the retry budget), else give up audibly. */
    silent(responseId) {
        const a = this.active;
        if (!a || a.responseId !== responseId)
            return;
        a.timer();
        a.silence = null;
        this.active = null;
        this.cancelled.add(responseId);
        this.wire.cancelResponse(responseId);
        this.batches.delete(responseId);
        const want = a.want;
        this.logger.error({ responseId, intent: want?.intent ?? 'auto' }, 'openai response produced nothing in time — cancelled');
        if (!want) {
            this.events.onGiveUp?.('caller_turn', 'stalled');
            return;
        }
        const attempts = want.attempts + 1;
        if (attempts > this.policy.maxRetries) {
            this.events.onGiveUp?.(want.intent, 'stalled');
            return;
        }
        if (!this.wanted || PRIORITY[want.intent] >= PRIORITY[this.wanted.intent])
            this.wanted = { ...want, attempts };
        this.pump();
    }
    stalled(which, id) {
        if (which === 'inflight' && this.inflight?.eventId === id) {
            const want = this.inflight.want;
            this.inflight = null;
            this.logger.error({ intent: want.intent }, 'openai never acknowledged a response request');
            this.events.onGiveUp?.(want.intent, 'stalled');
        }
        else if (which === 'active' && this.active?.responseId === id) {
            const want = this.active.want;
            this.active = null;
            this.batches.delete(id);
            this.logger.error({ responseId: id, intent: want?.intent }, 'openai response never finished');
            this.events.onGiveUp?.(want?.intent ?? 'caller_turn', 'stalled');
        }
        this.pump();
    }
    changed() {
        this.events.onActivityChanged?.();
    }
}
//# sourceMappingURL=ResponseCoordinator.js.map